"use client"
import React, { useState, createContext, useContext } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { Link, type LinkProps } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { isLightTheme } from '@/lib/theme'
import { getLogo } from '@/modules/branding/brandingService'

type SidebarContextProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  animate: boolean
}

const SidebarContext = createContext<SidebarContextProps | undefined>(undefined)

export function useSidebar() {
  const ctx = useContext(SidebarContext)
  if (!ctx) throw new Error('useSidebar must be used within a SidebarProvider')
  return ctx
}

export function SidebarProvider({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode
  open?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  animate?: boolean
}) {
  const [openState, setOpenState] = useState(false)
  const open = openProp !== undefined ? openProp : openState
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState
  return (
    <SidebarContext.Provider value={{ open, setOpen, animate }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function Sidebar({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode
  open?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  animate?: boolean
}) {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  )
}

export function SidebarBody(
  props: React.ComponentProps<typeof motion.div> & { showMobileHeader?: boolean }
) {
  const { showMobileHeader = true, ...rest } = props as any
  return (
    <>
      <DesktopSidebar {...(rest as any)} />
      <MobileSidebar showHeader={showMobileHeader} {...(rest as React.ComponentProps<'div'>)} />
    </>
  )
}

export function DesktopSidebar({
  className,
  children,
  ...props
}: React.ComponentProps<typeof motion.div>) {
  const { open, setOpen, animate } = useSidebar()
  // Evita flicker ao sair/entrar rapidamente do menu no desktop
  const closeTimer = React.useRef<number | null>(null)
  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const handleEnter = () => {
    clearCloseTimer()
    setOpen(true)
  }
  const handleLeave = () => {
    clearCloseTimer()
    // pequeno delay para evitar abre/fecha contínuo (flicker)
    closeTimer.current = window.setTimeout(() => setOpen(false), 200)
  }
  return (
    <motion.div
      className={cn(
        'hidden min-h-screen w-[340px] flex-shrink-0 md:flex md:flex-col md:sticky md:top-0 border-r border-border bg-muted/30 backdrop-blur-sm',
        className,
      )}
      animate={{ width: animate ? (open ? '340px' : '80px') : '340px' }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      {...props}
    >
      {children}
    </motion.div>
  )
}

export function MobileSidebar({
  className,
  children,
  showHeader = true,
  ...props
}: React.ComponentProps<'div'> & { showHeader?: boolean }) {
  const { open, setOpen } = useSidebar()
  return (
    <>
      {showHeader && (
        <div
          className={cn(
            'flex h-12 w-full flex-row items-center justify-between border-b border-border bg-muted/30 px-4 py-3 md:hidden',
          )}
          {...props}
        >
          <div className="z-20 flex w-full justify-end">
            <Menu
              className="h-5 w-5 cursor-pointer text-foreground"
              onClick={() => setOpen(!open)}
            />
          </div>
        </div>
      )}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className={cn(
              // Importante: esconder no desktop para não interferir no hover do sidebar
              'fixed inset-0 z-[100] flex h-full w-full flex-col justify-between bg-background p-6 md:hidden',
              className,
            )}
          >
            <button
              className="absolute right-6 top-4 z-50 text-foreground"
              onClick={() => setOpen(!open)}
              aria-label="Fechar menu"
            >
              <X className="h-5 w-5" />
            </button>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export type SidebarLinkItem = {
  label: string
  to: string
  icon?: React.ReactNode
  onClick?: () => void
}

export function SidebarLink({
  link,
  className,
  ...props
}: {
  link: SidebarLinkItem
  className?: string
  props?: LinkProps
}) {
  const { open, animate } = useSidebar()
  const content = (
    <>
      {/* Ajuste de tamanho do ícone para ~26px */}
      {React.isValidElement(link.icon)
        ? React.cloneElement(link.icon as any, { className: cn('h-[26px] w-[26px] flex-shrink-0 text-foreground', (link.icon as any).props?.className) })
        : link.icon}
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="inline-block text-[18px] text-foreground group-hover/sidebar:translate-x-1 !m-0 !p-0 transition duration-150 whitespace-pre"
      >
        {link.label}
      </motion.span>
    </>
  )
  if (link.onClick) {
    return (
      <button
        type="button"
        onClick={link.onClick}
        className={cn('group/sidebar flex items-center justify-start gap-2 py-2 text-left', className)}
      >
        {content}
      </button>
    )
  }
  return (
    <Link to={link.to} className={cn('group/sidebar flex items-center justify-start gap-2 py-2', className)} {...props}>
      {content}
    </Link>
  )
}

export function SidebarSection({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('flex flex-col gap-2', className)}>{children}</div>
}

export function SidebarLogo({ href = '/' }: { href?: string }) {
  const { open } = useSidebar()
  const [iconUrl, setIconUrl] = React.useState<string | null>(null)
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null)
  React.useEffect(() => {
    ;(async () => {
      setIconUrl(await getLogo('sidebar_icon', isLightTheme()))
      setLogoUrl(await getLogo('sidebar_logo', isLightTheme()))
    })()
  }, [])
  return (
    <Link to={href} className="relative z-20 flex items-center space-x-2 py-1 font-normal text-sm">
      {iconUrl ? (
        <img src={iconUrl} alt="menu icon" className="h-[1.75rem] w-auto flex-shrink-0" />
      ) : (
        <div className="h-[1.75rem] w-6 flex-shrink-0 rounded-br-lg rounded-tr-sm rounded-tl-lg rounded-bl-sm bg-foreground" />
      )}
      {open && logoUrl && (
        <motion.img
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          src={logoUrl}
          alt="menu logo"
          className="h-[1.85rem] w-auto flex-shrink-0"
        />
      )}
    </Link>
  )
}
